import type { ReactElement, ReactNode } from 'react'
import React, { useCallback, useRef, useState } from 'react'

import { PIE_CHART_SLICE_COLORS } from './constants'
import { PieChartContext } from './pie-chart-context'
import { PieChartPie } from './pie-chart-pie'
import type { PieChartHoveredState } from './types'

interface PieChartProps {
  width: number
  height: number
  colors?: string[]
  children: ReactNode
}

export const PieChart: React.FC<PieChartProps> = ({ width, height, colors, children }) => {
  const [hovered, setHovered] = useState<PieChartHoveredState | null>(null)
  const [tooltipHovered, setTooltipHovered] = useState<PieChartHoveredState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Separate out any <Tooltip> child so it renders above the <svg>
  const allChildren = React.Children.toArray(children) as ReactElement[]
  const pieChild = allChildren.find((x) => x.type === PieChartPie)
  const otherChildren = allChildren.filter((x) => x !== pieChild)

  const handleMouseLeave = useCallback(() => {
    setHovered((prev) => {
      if (!prev) return null
      if (prev.locked) return prev
      return null
    })
  }, [])

  return (
    <PieChartContext.Provider
      value={{
        width,
        height,
        hovered,
        setHovered,
        tooltipHovered,
        setTooltipHovered,
        colors: colors ?? PIE_CHART_SLICE_COLORS,
      }}
    >
      <div ref={containerRef} style={{ position: 'relative', width, height }}>
        <svg width={width} height={height} onMouseLeave={handleMouseLeave}>
          {pieChild && React.cloneElement(pieChild, { key: 'pie' })}
        </svg>

        {otherChildren.map((child, i) => {
          return React.cloneElement(child, { key: child.key ?? i, container: containerRef })
        })}
      </div>
    </PieChartContext.Provider>
  )
}
